import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock,
  Unlock,
  Copy,
  Check,
  AlertTriangle,
  Flame,
  Clock,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { publicApi as api } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { decryptSecret } from "../lib/crypto";
import { useTranslation, Trans } from "react-i18next";
import i18n from "../i18n";

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    if (!expiresAt) return;
    const target = new Date(expiresAt).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setRemaining(i18n.t("share:reveal.expired"));
        return;
      }
      const h = Math.floor(diff / 3.6e6);
      const m = Math.floor((diff % 3.6e6) / 6e4);
      const s = Math.floor((diff % 6e4) / 1000);
      setRemaining(
        h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return remaining;
}


export const SecretRevealPage = () => {
  const { secretId } = useParams();
  const { t } = useTranslation("share");
  const DECRYPT_ERROR = t("reveal.decryptError");
  const location = useLocation();
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [secret, setSecret] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // La clé de déchiffrement vit UNIQUEMENT en mémoire JS (jamais localStorage/session/cookie).
  const keyRef = useRef(null);
  // Indique si un fragment (#clé) était présent à l'arrivée sur la page.
  const [hasKey, setHasKey] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const revealTokenRef = useRef(null);

  const countdown = useCountdown(meta?.expires_at);

  // Extraction de la clé depuis le fragment, PUIS nettoyage immédiat de l'URL.
  // La clé n'est jamais envoyée à l'API. Un rechargement de page la perd (accepté).
  // On réagit aussi à un changement de fragment (navigation SPA vers la même route).
  useEffect(() => {
    const raw = window.location.hash || "";
    const key = raw.startsWith("#") ? raw.slice(1) : raw;
    if (key) {
      keyRef.current = key;
      setHasKey(true);
      setError(null);
      // Retire le fragment de la barre d'adresse (referrer/analytics/historique).
      try {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search
        );
      } catch (e) {
        /* noop */
      }
    }
  }, [location]);

  const loadMeta = useCallback(async () => {
    try {
      // GET métadonnées uniquement au chargement — ne brûle JAMAIS le secret.
      const { data } = await api.get(`/secrets/${secretId}`);
      setMeta(data);
    } catch (e) {
      setError(e.response?.status === 404 ? t("reveal.notFound") : t("reveal.consumedError"));
    } finally {
      setLoadingMeta(false);
    }
  }, [secretId]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const reveal = async () => {
    if (!keyRef.current) {
      setError(DECRYPT_ERROR);
      return;
    }
    setRevealing(true);
    try {
      // Reveal atomique (burn-after-read) : renvoie le ciphertext, jamais le clair.
      const headers = otpVerified && revealTokenRef.current
        ? { "X-Recipient-Reveal-Token": revealTokenRef.current }
        : {};
      const { data } = await api.post(`/secrets/${secretId}/reveal`, {}, { headers });
      // Déchiffrement EXCLUSIVEMENT local avec la clé du fragment.
      const decrypted = await decryptSecret({
        ciphertext: data.ciphertext,
        iv: data.iv,
        key: keyRef.current,
      });
      setSecret(decrypted);
    } catch (e) {
      // Message générique : ne jamais divulguer la clé ni les détails cryptographiques.
      const status = e.response?.status;
      if (status === 410 || status === 404) {
        setError(t("reveal.consumedError"));
      } else {
        setError(DECRYPT_ERROR);
      }
    } finally {
      setRevealing(false);
    }
  };

  const sendCode = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/secrets/${secretId}/recipient-otp/request`);
      setCodeSent(true);
      setMaskedEmail(data.masked_email || "");
      setCooldown(60);
      toast.success(t("reveal.codeSent"));
    } catch (e) {
      toast.error(t("reveal.sendError"));
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/secrets/${secretId}/recipient-otp/verify`, { code: otpCode });
      revealTokenRef.current = data.reveal_token;
      setOtpVerified(true);
      toast.success(t("reveal.verified"));
    } catch (e) {
      toast.error(t("reveal.invalidCode"));
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const copySecret = async () => {
    const ok = await copyToClipboard(secret.password);
    if (!ok) {
      toast.error(t("reveal.copyError"));
      return;
    }
    setCopied(true);
    toast.success(t("reveal.copiedToast"));
    setTimeout(() => setCopied(false), 2000);
  };

  const alreadyGone =
    !secret && meta && (meta.status !== "active" || meta.expired);

  return (
    <div className="app-bg grain min-h-screen flex flex-col items-center justify-center px-4 py-16 relative">
      <Link
        to="/"
        className="flex items-center gap-2 mb-8 text-slate-500 hover:text-slate-900 transition-colors relative z-10"
      >
        {meta?.branding?.logo_url ? (
          <img src={meta.branding.logo_url} alt="Workspace" className="max-h-12 max-w-[220px] object-contain" />
        ) : (
          <>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="font-heading font-bold">SecurePass</span>
          </>
        )}
      </Link>

      <motion.div
        data-testid="reveal-secret-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="glass rounded-2xl p-8 w-full max-w-md relative z-10"
      >
        {loadingMeta ? (
          <div className="flex flex-col items-center py-8 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            {t("reveal.checking")}
          </div>
        ) : error && !secret ? (
          <div
            data-testid="expired-error-message"
            className="flex flex-col items-center text-center py-6"
          >
            <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">{t("reveal.unavailable")}</h2>
            <p className="text-sm text-slate-500 leading-relaxed">{error}</p>
          </div>
        ) : secret ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4 mx-auto">
              <Unlock className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-1">{t("reveal.unlockedTitle")}</h2>
            <p className="text-xs text-slate-500 mb-5">
              {t("reveal.copyNow")}
            </p>

            {secret.note && (
              <div className="text-left rounded-lg bg-slate-50 border border-slate-200 p-3 mb-4 text-sm text-slate-600">
                {secret.note}
              </div>
            )}

            <div
              data-testid="revealed-password-box"
              className="ph-no-capture font-mono-token text-lg font-semibold tracking-wider break-all bg-slate-50 border border-emerald-500/25 rounded-xl p-4 mb-4 text-emerald-600"
            >
              {secret.password}
            </div>

            <Button
              data-testid="btn-copy-revealed-secret"
              onClick={copySecret}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? t("reveal.copied") : t("reveal.copyPassword")}
            </Button>

            <div
              data-testid="status-burned-badge"
              className="flex items-center justify-center gap-2 mt-5 text-xs text-red-600"
            >
              <Flame className="w-3.5 h-3.5" />
              {t("reveal.burned")}
            </div>
          </motion.div>
        ) : alreadyGone ? (
          <div
            data-testid="expired-error-message"
            className="flex flex-col items-center text-center py-6"
          >
            <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
              <Flame className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">
              {meta.expired ? t("reveal.expiredTitle") : t("reveal.consumedTitle")}
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              {meta.expired
                ? t("reveal.expiredText")
                : t("reveal.consumedText")}
            </p>
          </div>
        ) : !hasKey ? (
          <div
            data-testid="missing-key-message"
            className="flex flex-col items-center text-center py-6"
          >
            <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">{t("reveal.incomplete")}</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              {DECRYPT_ERROR}
            </p>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-4 mx-auto">
              <Lock className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">
              {t("reveal.waitingTitle")}
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-5">
              <Trans t={t} i18nKey="reveal.oneShot" components={{ b: <b className="text-slate-900" />, r: <b className="text-red-600" /> }} />
            </p>

            <div
              data-testid="reveal-countdown-timer"
              className="flex items-center justify-center gap-2 mb-6 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg py-2.5"
            >
              <Clock className="w-4 h-4 text-cyan-600" />
              {t("reveal.expiresIn")} <span className="font-mono-token text-cyan-600">{countdown}</span>
            </div>

            {meta.recipient_verification === "email_otp" && !otpVerified ? (
              <div data-testid="recipient-otp-gate" className="text-left">
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 mb-4 text-sm text-slate-600">
                  {t("reveal.otpRequired")}{maskedEmail ? ` (${maskedEmail})` : ""}.
                </div>
                {!codeSent ? (
                  <Button
                    data-testid="send-otp-code-btn"
                    onClick={sendCode}
                    disabled={sending}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                  >
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {t("reveal.sendCode")}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <input
                      data-testid="recipient-otp-input"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full text-center text-xl tracking-[0.4em] font-mono-token rounded-md bg-slate-50 border border-slate-200 px-3 py-2 outline-none focus:border-emerald-400"
                    />
                    <Button
                      data-testid="verify-otp-btn"
                      onClick={verifyCode}
                      disabled={verifying || otpCode.length !== 6}
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                    >
                      {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      {t("reveal.verify")}
                    </Button>
                    <button
                      data-testid="resend-otp-btn"
                      onClick={sendCode}
                      disabled={cooldown > 0 || sending}
                      className="w-full text-xs text-slate-500 hover:text-emerald-600 disabled:opacity-50"
                    >
                      {cooldown > 0 ? t("reveal.resendIn", { s: cooldown }) : t("reveal.resend")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {meta.recipient_verification === "email_otp" && otpVerified && (
                  <div data-testid="otp-verified-badge" className="flex items-center justify-center gap-2 mb-4 text-sm text-emerald-600">
                    <Check className="w-4 h-4" /> {t("reveal.verified")}
                  </div>
                )}
                <Button
                  data-testid="btn-reveal-secret"
                  onClick={reveal}
                  disabled={revealing}
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-emerald-950 font-bold"
                >
                  {revealing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Unlock className="w-4 h-4 mr-2" />
                  )}
                  {t("reveal.unlock")}
                </Button>
              </>
            )}
          </div>
        )}
      </motion.div>
      {meta?.branding?.logo_url && (
        <div className="mt-5 text-xs text-slate-400 flex items-center gap-1.5 relative z-10">
          <ShieldCheck className="w-3.5 h-3.5" />
          Sécurisé par SecurePass
        </div>
      )}
    </div>
  );
};
